// authgarantia.js

// ===== Config base =====
const apiUrlRaw = process.env.REACT_APP_API_URL;
const apiUrl = apiUrlRaw ? apiUrlRaw.replace(/\/+$/, "") : "";

// Lee token desde localStorage / sessionStorage.
// Ajusta las claves si tu login guarda con otro nombre.
function getStoredToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("accessToken") ||
    null
  );
}

// Si prefieres setear manualmente desde tu flujo de login:
export function setAuthToken(token, { persist = true } = {}) {
  if (persist) {
    localStorage.setItem("token", token);
  } else {
    sessionStorage.setItem("token", token);
  }
}
export function clearAuthToken() {
  localStorage.removeItem("token");
  localStorage.removeItem("accessToken");
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("accessToken");
}

// ===== Parser robusto =====
async function parseResponseOrThrow(response) {
  const url = response.url || "unknown";
  const statusText = `${response.status} ${response.statusText}`;
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const raw = await response.text();
  const hasBody = typeof raw === "string" && raw.length > 0;
  const seemsJson = contentType.includes("application/json");

  if (!response.ok) {
    if (seemsJson && hasBody) {
      try {
        const j = JSON.parse(raw);
        const msg = j?.message || j?.mensaje || j?.error || JSON.stringify(j);

        // Caso especial 401 para que el componente muestre algo útil
        if (response.status === 401) {
          throw new Error(`No autorizado (401): ${msg}`);
        }
        throw new Error(`API error ${statusText} en ${url}: ${msg}`);
      } catch (e) {
        throw new Error(
          `API error ${statusText} en ${url}: JSON inválido (${e.message}). Body: ${raw.slice(
            0,
            200
          )}`
        );
      }
    }
    const snippet = hasBody ? raw.slice(0, 200) : "";
    throw new Error(
      `API error ${statusText} en ${url}: Respuesta no-JSON (${contentType || "sin Content-Type"}). Body: ${snippet}`
    );
  }

  if (seemsJson) {
    if (!hasBody) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      throw new Error(`JSON inválido (${statusText}) en ${url}: ${e.message}`);
    }
  }
  return raw; // por si algún endpoint devuelve texto
}

// ===== Helpers de request =====
function buildUrl(path) {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${apiUrl}${cleanPath}`;
}

function buildHeaders(userHeaders = {}) {
  const headers = new Headers(userHeaders);
  // Asegura Accept por defecto
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  // Adjunta Authorization si no está y tenemos token
  if (!headers.has("Authorization")) {
    const token = getStoredToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

/**
 * request(path, options)
 * - Envía cookies (credentials: 'include') para sesiones basadas en cookie.
 * - Adjunta Bearer token si existe en storage.
 */
async function request(path, options = {}) {
  const url = buildUrl(path);
  const { headers: userHeaders, credentials, ...rest } = options;

  const res = await fetch(url, {
    credentials: credentials ?? "include", // 👈 importante para cookies
    headers: buildHeaders(userHeaders),
    ...rest,
  });

  return parseResponseOrThrow(res);
}

// ===== Endpoints Garantías (CRUD) =====

export async function createGarantia(data) {
  return request("/api/garantias/create", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function getGarantias() {
  return request("/api/garantias/list", {
    method: "GET",
  });
}

export async function getGarantiaByNumFolio(numFolio) {
  return request(`/api/garantias/view/${encodeURIComponent(numFolio)}`, {
    method: "GET",
  });
}

export async function uploadImages(numFolio, imageMap) {
  const formData = new FormData();

  if (Array.isArray(imageMap)) {
    imageMap.forEach((file) => formData.append("images", file));
  } else if (imageMap && typeof imageMap === "object") {
    Object.keys(imageMap).forEach((fieldName) => {
      const file = imageMap[fieldName];
      if (file) formData.append(fieldName, file);
    });
  }

  return request(`/api/garantias/upload-images/${encodeURIComponent(numFolio)}`, {
    method: "POST",
    body: formData,
  });
}

export async function generatePDF(numFolio) {
  return request(`/api/garantias/generate-pdf/${encodeURIComponent(numFolio)}`, {
    method: "GET",
  });
}

export async function savePDF(numFolio, pdfBase64) {
  return request(`/api/garantias/save-pdf/${encodeURIComponent(numFolio)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pdfBase64 }),
  });
}

export async function updateGarantia(numFolio, data) {
  return request(`/api/garantias/update/${encodeURIComponent(numFolio)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function deleteGarantia(numFolio) {
  return request(`/api/garantias/delete/${encodeURIComponent(numFolio)}`, {
    method: "DELETE",
  });
}
